import { clsx, type ClassValue } from "clsx"
import { subMonths } from "date-fns"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Creates a UTC date object from a date string or Date object
 * @param date - Date string or Date object
 * @returns UTC Date object
 */
export function createUTCDate(date: string | Date): Date {
  if (typeof date === 'string') {
    // Parse the date string and create UTC date
    // Handle both ISO format and simple date strings
    if (date.includes('T') || date.includes('Z')) {
      // Already in ISO format
      return new Date(date);
    } else {
      // Simple date string like "2025-07-01", treat as UTC date
      // Add 'T00:00:00.000Z' to make it a proper UTC ISO string
      return new Date(date + 'T00:00:00.000Z');
    }
  }
  // If it's already a Date object, convert to UTC
  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
}

/**
 * Gets the current UTC date
 * @returns Current UTC Date object
 */
export function getCurrentUTCDate(): Date {
  const now = new Date();
  return new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
}

/**
 * Converts a date string to a Date object
 * @param dateString - Date string to convert
 * @returns Date object
 */
export function parseDateFromString(dateString: string | undefined): Date | undefined {
  if (!dateString) {
    return undefined;
  }
  if (dateString.includes('T') || dateString.includes('Z')) {
    return new Date(dateString);
  }
  // Calendar date only (`yyyy-MM-dd`): parse as local midnight so US TZ matches billing month.
  const [y, m, d] = dateString.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) {
    return new Date(dateString + 'T00:00:00.000Z');
  }
  return new Date(y, m - 1, d);
}

/**
 * Converts an exclusive period end (start of next period, midnight UTC)
 * to an inclusive end-of-previous-day UTC timestamp.
 * e.g. `2026-10-01T00:00:00Z` → `2026-09-30T23:59:59Z`
 */
export function toInclusivePeriodEnd(dateString: string | null | undefined): string | null {
  if (!dateString) return null;

  const normalizedInput = dateString.includes('T') || dateString.includes('Z')
    ? dateString
    : `${dateString}T00:00:00.000Z`;
  const date = new Date(normalizedInput);
  if (Number.isNaN(date.getTime())) return null;

  const isExclusiveMidnight =
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;

  if (isExclusiveMidnight) {
    date.setUTCSeconds(-1);
  }

  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Local midnight Date from a UTC ISO string's UTC Y/M/D.
 * Use for calendars so `2026-09-30T23:59:59Z` shows as Sep 30 (not Oct 1 in UTC+).
 */
export function utcIsoToLocalCalendarDate(dateString: string | null | undefined): Date | undefined {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Billing period month key (`yyyy-MM`) from API `startDate`.
 * Safe for `yyyy-MM-dd` strings — no timezone shift on month boundaries.
 */
export function currentMonthKeyFromStartDate(startDate: string): string {
  return startDate.slice(0, 7);
}

/** Local calendar month key (`yyyy-MM`) for a reference instant (default: now). */
export function localMonthKey(reference: Date = new Date()): string {
  const y = reference.getFullYear();
  const m = String(reference.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** Whether `startDate` falls in the same calendar month as `reference` (local TZ). */
export function isCurrentMonth(startDate: string, reference: Date = new Date()): boolean {
  return currentMonthKeyFromStartDate(startDate) === localMonthKey(reference);
}

/** Whether `startDate` falls in the calendar month before `reference` (local TZ). */
export function isPreviousMonth(startDate: string, reference: Date = new Date()): boolean {
  return currentMonthKeyFromStartDate(startDate) === localMonthKey(subMonths(reference, 1));
}

/**
 * Converts a date string to a locale date string
 * @param dateString - Date string to convert
 * @param options - Options for the locale date string
 * @returns Locale date string
 */
export function formatDateToLocaleString(dateString: string | undefined, options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }): string | undefined {
  if (!dateString) {
    return undefined;
  }
  return new Date(parseDateFromString(dateString)).toLocaleString('en-US', options);
}